/**
 * List text the result cards replace: `[[list:<tool>]]` … `[[/list]]`.
 *
 * On the website the backend marks the rows of a search or room list (and the
 * "Showing the top N by default" line that counts them) with a line before and
 * a line after. While the cards for that list are on screen the bubble hides
 * the marked text; without them — the turn carried another tool's cards, or
 * the conversation was reloaded and the cards are gone — the rows show, the
 * marker lines dropped. The saved message keeps the rows either way.
 *
 * The cards only arrive with `bot:done`, after the text has streamed, so a
 * streaming bubble hides every marked block (see `hideCardLists`); a block
 * whose tool turns out to carry no cards comes back when the turn settles.
 *
 * Token shapes are owned by sky360-ms `bot-agent/card-list-markup.ts` — change
 * them there and here together.
 */

/** The tools whose marked lists are hidden, or 'all' while streaming. */
export type CardListHide = 'all' | readonly string[];

const OPEN_RE = /\[\[list:([a-z_]+)\]\]\n?/g;
const CLOSE = '[[/list]]';
const STRAY_CLOSE_RE = /\n?\[\[\/list\]\]/g;
/** A marker line still streaming in: `[`, `[[`, `[[li`, `[[list:search_fl`… */
const PARTIAL_OPEN_RE = /\[(?:\[(?:l(?:i(?:s(?:t(?::[a-z_]*)?)?)?)?)?)?$/;

const leadingNewlines = (s: string, from: number): number => {
  let n = 0;
  while (s[from + n] === '\n') n++;
  return n;
};

const trailingNewlines = (s: string): number => {
  let n = 0;
  while (s[s.length - 1 - n] === '\n') n++;
  return n;
};

const trimTrailingNewlines = (s: string): string => s.slice(0, s.length - trailingNewlines(s));

/**
 * `text` as the bubble shows it: blocks of a hidden tool removed, the marker
 * lines of every other block removed with their rows kept.
 *
 * Removing a block leaves its surroundings spaced as if the block had never
 * been there: the line breaks on either side merge into the larger of the two
 * (at most one blank line), and none are left at the start or end.
 *
 * `streaming`: the text is still arriving. A block whose closing line has not
 * come yet is hidden to the end, and a marker cut off mid-token is held back.
 * Once settled, a block the model never closed is shown — a list with nowhere
 * to end is safer visible than half the reply hidden.
 */
export function hideCardLists(text: string, hide: CardListHide, streaming = false): string {
  if (!text) return text;
  if (text.indexOf('[[') === -1 && !(streaming && text.endsWith('['))) return text;

  const isHidden = (tool: string) => hide === 'all' || hide.includes(tool);
  let out = '';
  // Line breaks owed before the next visible text, where a block was removed.
  let pending = 0;
  const append = (chunk: string) => {
    if (!chunk) return;
    if (pending && out) out += '\n'.repeat(pending);
    pending = 0;
    out += chunk;
  };

  let cursor = 0;
  OPEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = OPEN_RE.exec(text))) {
    append(text.slice(cursor, match.index).replace(STRAY_CLOSE_RE, ''));
    const bodyStart = match.index + match[0].length;
    const closeAt = text.indexOf(CLOSE, bodyStart);
    const hidden = isHidden(match[1]);

    if (closeAt === -1) {
      if (streaming && hidden) return trimTrailingNewlines(out);
      // Never closed: drop the stray opening line, keep what follows.
      cursor = bodyStart;
      continue;
    }

    const after = closeAt + CLOSE.length;
    if (hidden) {
      const before = Math.max(pending, trailingNewlines(out));
      out = trimTrailingNewlines(out);
      const next = leadingNewlines(text, after);
      pending = Math.min(2, Math.max(before, next));
      cursor = after + next;
    } else {
      let body = text.slice(bodyStart, closeAt);
      if (body.endsWith('\n')) body = body.slice(0, -1);
      append(body);
      cursor = after;
    }
    OPEN_RE.lastIndex = cursor;
  }

  let tail = text.slice(cursor).replace(STRAY_CLOSE_RE, '');
  // A marker still arriving goes like the block it opens, line breaks included.
  if (streaming && PARTIAL_OPEN_RE.test(tail)) {
    tail = trimTrailingNewlines(tail.replace(PARTIAL_OPEN_RE, ''));
  }
  append(tail);
  return out;
}
